import * as React from 'react'
import { styled } from 'next-yak'

import * as S from '../styles'

import { SliderButton, type SliderButtonProps } from './button/SliderButton'

export type CarouselProps = React.HTMLAttributes<HTMLDivElement> & {
  count?: number
  isPrevDisabled?: boolean
  isNextDisabled?: boolean
  fixedControls?: boolean
  hideControls?: boolean
  labels?: string[]
  onCountChange?: (newCount: number) => void
  sliderButtonSize?: SliderButtonProps['size']
}

function ControlledCarousel({
  children,
  count,
  isPrevDisabled,
  isNextDisabled,
  fixedControls,
  hideControls,
  labels,
  onCountChange,
  sliderButtonSize = 'medium',
  ...rest
}: CarouselProps & {
  count: number
  onCountChange: (newCount: number) => void
}): React.ReactElement<any> {
  const [animation, setAnimation] = React.useState('fade')

  const slides = React.Children.toArray(children)
  const slidesLength = slides.length

  const label = labels?.[count]

  React.useEffect(() => {
    if (onCountChange) {
      onCountChange(count)
    }
  }, [count, onCountChange])

  return (
    <S.carousel.Wrapper {...rest}>
      {!hideControls && (
        <S.carousel.ControlPrev fixedControls={fixedControls}>
          <SliderButton
            direction="left"
            size={sliderButtonSize}
            disabled={isPrevDisabled}
            type="button"
            onClick={() => {
              setAnimation('prev')
              count === 0 ? onCountChange(slidesLength - 1) : onCountChange(count - 1)
            }}
          />
        </S.carousel.ControlPrev>
      )}
      <S.carousel.Slide data-animation={animation}>{slides[count]}</S.carousel.Slide>
      {!hideControls && (
        <S.carousel.ControlNext fixedControls={fixedControls}>
          <SliderButton
            direction="right"
            size={sliderButtonSize}
            disabled={isNextDisabled}
            type="button"
            onClick={() => {
              setAnimation('next')
              count === slidesLength - 1 ? onCountChange(0) : onCountChange(count + 1)
            }}
          />
        </S.carousel.ControlNext>
      )}
      {(label || slidesLength > 1) && (
        <S.carousel.LabelContainer>
          <S.carousel.Label>
            {label} {label && slidesLength > 1 && '-'} {slidesLength > 1 && `${count + 1}/${slidesLength}`}
          </S.carousel.Label>
        </S.carousel.LabelContainer>
      )}
    </S.carousel.Wrapper>
  )
}

function UncontrolledCarousel(props: CarouselProps & { initialCount?: number }): React.ReactElement<any> {
  const [count, setCount] = React.useState(props.initialCount ?? 0)

  return <ControlledCarousel count={count} onCountChange={setCount} {...props} />
}

function CarouselComponent({ count, onCountChange, ...props }: CarouselProps): React.ReactElement<any> {
  if (count !== undefined && onCountChange !== undefined) {
    return <ControlledCarousel {...props} count={count} onCountChange={onCountChange} />
  } else {
    return <UncontrolledCarousel {...props} />
  }
}

export const Carousel = styled(CarouselComponent)``
